import { saveFilesToZip } from './shared/saveFilesToZip';
import {
  getOfferFileDownloadList,
  getOffersBatchDownloadUrls,
} from '~src/services/api/offers';
import { getVoucherAttachmentDownloadUrl } from '~src/services/api/contracts';
import { Modal } from 'antd';

// Sanitize filenames by replacing invalid filesystem characters
function sanitizeFileName(name: string): string {
  // Replace invalid filesystem characters: / \ : * ? " < > |
  return name.replace(/[/\\:*?"<>|]/g, '_');
}

// Retry function with exponential backoff
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<T> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries - 1) throw error;
      const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw new Error('Max retries exceeded');
}

async function withConcurrency<T>(
  tasks: (() => Promise<T>)[],
  concurrency: number
): Promise<{ results: (T | null)[]; failedIndices: number[] }> {
  if (tasks.length === 0) return { results: [], failedIndices: [] };

  const results: (T | null)[] = new Array(tasks.length).fill(null);
  const failedIndices: number[] = [];
  let index = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, tasks.length) },
    async () => {
      while (index < tasks.length) {
        const i = index++;
        try {
          results[i] = await tasks[i]();
        } catch (error) {
          console.error(`Task ${i} failed:`, error);
          failedIndices.push(i);
          results[i] = null;
        }
      }
    }
  );

  await Promise.all(workers);
  return { results, failedIndices };
}

export type OfferMergedVoucherLike = {
  id: number;
  name: string;
  projectAttachments: any[];
  vendorAttachments?: any[];
};

export async function downloadOfferZip({
  projectId,
  tenderId,
  contractId,
  offerId,
  contractName,
  offerName,
  mergedVouchers,
  offerSerial,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  contractName: string;
  offerName: string;
  mergedVouchers: OfferMergedVoucherLike[];
  offerSerial?: string;
}): Promise<void> {
  // 1. Get all vendor file metadata for this offer
  const fileList = await getOfferFileDownloadList({
    projectId,
    tenderId,
    offerId,
  });

  // 2. Batch-resolve vendor S3 URLs in one POST
  const urlEntries =
    fileList.length > 0
      ? await getOffersBatchDownloadUrls({
          projectId,
          tenderId,
          using_keys: fileList.map((f) => f.using_key),
        })
      : [];
  const urlByKey = Object.fromEntries(urlEntries.map((e) => [e.key, e.url]));

  // 3. Build voucher id → name map
  const voucherNameById = Object.fromEntries(
    mergedVouchers.map((v) => [v.id, v.name])
  );

  const rootFolder = `${contractName} - ${offerName}`;

  // 4. Vendor files → {rootFolder}/{VoucherName}/
  const vendorFiles = fileList.flatMap((f) => {
    const url = urlByKey[f.using_key];
    if (!url) return [];
    const voucherName = sanitizeFileName(
      voucherNameById[f.voucher_id] || `Voucher_${f.voucher_id}`
    );

    // Prepend offer serial to Excel files
    let fileName = f.file_name;
    if (offerSerial && /\.(xlsx?|xlsm|xltx?)$/i.test(fileName)) {
      const serialPrefix = offerSerial.replace('#', '');
      fileName = `${serialPrefix}_${fileName}`;
    }

    return [
      {
        location: url,
        name: fileName,
        path: `${rootFolder}/${voucherName}`,
      },
    ];
  });

  // Project files removed - only vendor files included
  const allFiles = vendorFiles;
  if (allFiles.length === 0) return;

  await saveFilesToZip(allFiles, rootFolder);
}

// ---- Bulk download: all offers for one contract ----

export type BulkOfferEntry = {
  offerId: number;
  offerName: string;
  mergedVouchers: OfferMergedVoucherLike[];
  offerSerial?: string;
};

export async function downloadAllOffersZip({
  projectId,
  tenderId,
  contractId,
  contractName,
  offersData,
  tabLabel,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  contractName: string;
  offersData: BulkOfferEntry[];
  tabLabel?: string;
}): Promise<void> {
  if (offersData.length === 0) return;

  const bulkRoot = tabLabel
    ? `${contractName} offers bulk - ${tabLabel}`
    : `${contractName} offers bulk`;

  // 1. Fetch vendor file lists for all offers — max 5 in flight at a time with retry logic
  const { results: fileListsPerOffer, failedIndices } = await withConcurrency(
    offersData.map(({ offerId }) => () =>
      retryWithBackoff(() =>
        getOfferFileDownloadList({ projectId, tenderId, offerId })
      )
    ),
    5 // Reduced from 20 to 5 to avoid overwhelming backend
  );

  // Show warning if any offers failed to download
  if (failedIndices.length > 0) {
    const failedOfferNames = failedIndices
      .map((idx) => offersData[idx]?.offerName)
      .filter(Boolean)
      .join(', ');

    Modal.warning({
      title: 'Some offers failed to download',
      content: `The following offers could not be included: ${failedOfferNames}. The ZIP will contain all other offers.`,
    });
  }

  // 2. Collect all using_keys and resolve S3 URLs in one batch POST
  const allUsingKeys = fileListsPerOffer
    .filter((list): list is NonNullable<typeof list> => list !== null)
    .flatMap((list) => list.map((f) => f.using_key));
  const urlEntries =
    allUsingKeys.length > 0
      ? await getOffersBatchDownloadUrls({
          projectId,
          tenderId,
          using_keys: allUsingKeys,
        })
      : [];
  const urlByKey = Object.fromEntries(urlEntries.map((e) => [e.key, e.url]));

  // 3. Build vendor files (sync — URLs already resolved)
  type FileEntry = { location: string; name: string; path: string };
  const allVendorFiles: FileEntry[] = [];

  offersData.forEach(({ offerName, offerSerial, mergedVouchers }, idx) => {
    const fileList = fileListsPerOffer[idx];
    if (!fileList) return; // Skip failed offers

    const offerRoot = `${bulkRoot}/${contractName} - ${offerName}`;
    const voucherNameById = Object.fromEntries(
      mergedVouchers.map((v) => [v.id, v.name])
    );

    // Vendor files — S3 URLs already in hand, no async needed
    fileList.forEach((f) => {
      const url = urlByKey[f.using_key];
      if (!url) return;
      const voucherName = sanitizeFileName(
        voucherNameById[f.voucher_id] || `Voucher_${f.voucher_id}`
      );

      // Prepend offer serial to Excel files
      let fileName = f.file_name;
      if (offerSerial && /\.(xlsx?|xlsm|xltx?)$/i.test(fileName)) {
        const serialPrefix = offerSerial.replace('#', '');
        fileName = `${serialPrefix}_${fileName}`;
      }

      allVendorFiles.push({
        location: url,
        name: fileName,
        path: `${offerRoot}/${voucherName}`,
      });
    });
  });

  // Project files removed - only vendor files included
  const allFiles = allVendorFiles;
  if (allFiles.length === 0) return;

  await saveFilesToZip(allFiles, bulkRoot);
}
